import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * Hook to handle Safari's aggressive WebSocket throttling.
 * 
 * Safari (and iOS Safari especially) aggressively throttles or kills WebSocket 
 * connections when:
 * - Tab goes to background
 * - Device sleeps
 * - Page loses focus for extended period
 * 
 * This hook listens for visibility changes and:
 * 1. Reconnects Supabase realtime channels when page becomes visible
 * 2. Invalidates stale queries to refresh data
 */
export function useRealtimeReconnect() {
  const queryClient = useQueryClient();
  const lastVisibleTime = useRef(Date.now());

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const hiddenDuration = Date.now() - lastVisibleTime.current;
        
        // If page was hidden for more than 30 seconds, reconnect and refresh
        // Safari typically kills connections after ~30s of background
        if (hiddenDuration > 30_000) {
          console.log('[Realtime] Page became visible after', Math.round(hiddenDuration / 1000), 'seconds - reconnecting...');
          
          // Force reconnect all Supabase realtime channels
          try {
            // Get all active channels and reconnect them
            const channels = supabase.getChannels();
            
            for (const channel of channels) {
              // Unsubscribe and resubscribe to force reconnection
              const channelName = channel.topic;
              await channel.unsubscribe();
              
              // Small delay to allow clean disconnection
              await new Promise(resolve => setTimeout(resolve, 100));
              
              // Resubscribe - the channel will reconnect automatically
              channel.subscribe();
              console.log('[Realtime] Reconnected channel:', channelName);
            }
          } catch (error) {
            console.error('[Realtime] Error reconnecting channels:', error);
          }

          // Invalidate all queries to refresh stale data
          queryClient.invalidateQueries();
          
          console.log('[Realtime] Invalidated all queries');
        } else if (hiddenDuration > 5_000) {
          // If hidden for 5-30 seconds, just invalidate queries without full reconnect
          // The WebSocket might still be alive but data could be stale
          queryClient.invalidateQueries();
          console.log('[Realtime] Page visible after', Math.round(hiddenDuration / 1000), 'seconds - refreshed queries');
        }
      } else {
        // Page is becoming hidden, record the time
        lastVisibleTime.current = Date.now();
      }
    };

    // Also handle page focus (works better on some Safari versions)
    const handleFocus = () => {
      const hiddenDuration = Date.now() - lastVisibleTime.current;
      
      if (hiddenDuration > 60_000) {
        // If window was unfocused for over a minute, refresh data
        queryClient.invalidateQueries();
        console.log('[Realtime] Window focused after', Math.round(hiddenDuration / 1000), 'seconds - refreshed queries');
      }
    };

    // Handle page show (back-forward cache restoration)
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        // Page was restored from back-forward cache
        console.log('[Realtime] Page restored from bfcache - refreshing...');
        queryClient.invalidateQueries();
        
        // Force reconnect channels after bfcache restore
        const channels = supabase.getChannels();
        channels.forEach(channel => {
          channel.unsubscribe();
          setTimeout(() => channel.subscribe(), 100);
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [queryClient]);
}










